import { useEffect } from "react";

/**
 * Reveal-on-scroll, in ~20 lines instead of an animation library.
 *
 * Anything with className="reveal" fades and lifts into place the first time it enters
 * the viewport. Stagger a group by setting `style={{ "--d": "80ms" }}` on each child.
 *
 * Two escape hatches, both important:
 *   - prefers-reduced-motion → everything is revealed immediately, no transition.
 *   - no IntersectionObserver (jsdom, ancient browsers) → same, revealed immediately.
 * So content is NEVER left invisible because the animation could not run.
 */
export function useScrollReveal(deps = []) {
    useEffect(() => {
        const nodes = Array.from(document.querySelectorAll(".reveal:not(.in)"));
        if (!nodes.length) return;

        const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

        if (reduced || typeof IntersectionObserver === "undefined") {
            nodes.forEach((n) => n.classList.add("in"));
            return;
        }

        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;
                    entry.target.classList.add("in");
                    io.unobserve(entry.target); // reveal once, never re-hide
                }
            },
            { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
        );

        nodes.forEach((n) => io.observe(n));
        return () => io.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

const MAX_TILT = 7; // degrees — past ~8 it stops reading as depth and starts reading as broken

/**
 * Pointer-tracking spotlight for cards: writes --mx/--my (in %) on the hovered element
 * so CSS can put a soft glow under the cursor. Pure CSS custom properties — no re-render.
 */
export function spotlight(e) {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
    el.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
}

/** Spotlight + a subtle 3D tilt toward the cursor. Also written as CSS variables. */
export function tilt(e) {
    spotlight(e);

    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;   // -0.5 … 0.5
    const py = (e.clientY - r.top) / r.height - 0.5;

    el.style.setProperty("--ry", `${px * MAX_TILT * 2}deg`);
    el.style.setProperty("--rx", `${-py * MAX_TILT * 2}deg`);
}

export function untilt(e) {
    const el = e.currentTarget;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
}
